import Chip from 'core-system/Chip'
import MonthToggle from 'core-system/DatePicker/MonthToggle'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import moment from 'moment'
import React, { useMemo } from 'react'
import { MyCommuterTransaction } from 'redux/employee/employeeTypes'
import FormattingUtils from 'shared/FormattingUtils'
import styled from 'styled-components'
import { useDrawerTableOperations } from './useDrawerTableOperations'
import {
  applyTransactionFilters,
  buildTransactionRows,
  TRANSACTION_SEARCH_KEYS,
  transactionBucketColor,
  transactionFilters,
  transactionStatusLabel,
  transactionStatusVariant,
} from './transactionRows'

const MonthBar = styled.div`
  margin-bottom: 1rem;
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const Line = styled.div`
  display: flex;
  align-items: center;
  min-height: 1.25rem;
`

const BucketLine = styled(Line)`
  gap: 0.5rem;
`

const Dot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={600}>
      {text}
    </Text>
  </TableRowCell>
)

interface MyCommuterDetailsTransactionsProps {
  currentDate: string
  onIncreaseClick: () => void
  onDecreaseClick: () => void
  commuterTransactions: MyCommuterTransaction[]
}

const MyCommuterDetailsTransactions = React.memo(
  (props: MyCommuterDetailsTransactionsProps) => {
    const {
      currentDate,
      commuterTransactions,
      onIncreaseClick,
      onDecreaseClick,
    } = props
    const transactions = commuterTransactions || []
    const rows = useMemo(
      () => buildTransactionRows(transactions),
      [transactions]
    )
    const { apply, bar } = useDrawerTableOperations({
      source: rows,
      searchKeys: TRANSACTION_SEARCH_KEYS,
      sortKey: 'created',
      buildFilters: transactionFilters,
      applyFilters: applyTransactionFilters,
      margin: '0 0 0.75rem',
    })
    const visibleRows = apply(rows)

    return (
      <>
        <MonthBar>
          <MonthToggle
            currentDate={currentDate}
            onIncreaseClick={onIncreaseClick}
            onDecreaseClick={onDecreaseClick}
          />
        </MonthBar>
        {bar}
        <Table
          maxRows={5}
          emptyMessage={
            visibleRows.length === 0 && rows.length > 0
              ? 'No matching transactions'
              : 'No transactions'
          }
          headerCells={[
            <TableRow key='transactions-header'>
              <HeaderCell text='Date' />
              <HeaderCell text='Merchant' />
              <HeaderCell text='Bucket' />
              <HeaderCell text='Amount' />
              <HeaderCell text='Status' />
            </TableRow>,
          ]}
          rows={visibleRows.map(({ transaction }) => {
            const buckets = transaction.buckets || []
            return (
              <TableRow key={transaction.internalId}>
                <TableRowCell noWrap>
                  <Text variant='action3'>
                    {moment(transaction.created).format('MMM D')}
                  </Text>
                </TableRowCell>
                <TableRowCell>
                  <Text variant='action3'>{transaction.name || '—'}</Text>
                </TableRowCell>
                <TableRowCell>
                  {buckets.length === 0 ? (
                    <Text variant='action3'>—</Text>
                  ) : (
                    <Stack>
                      {buckets.map((bucket, index) => (
                        <BucketLine
                          key={`${transaction.internalId}-${bucket.kind || bucket.name}-${index}`}
                        >
                          <Dot $color={transactionBucketColor(bucket.kind)} />
                          <Text variant='action3'>{bucket.name}</Text>
                        </BucketLine>
                      ))}
                    </Stack>
                  )}
                </TableRowCell>
                <TableRowCell noWrap>
                  {buckets.length === 0 ? (
                    <Text variant='action3'>
                      {FormattingUtils.formatDollar(transaction.spend)}
                    </Text>
                  ) : (
                    <Stack>
                      {buckets.map((bucket, index) => (
                        <Line
                          key={`${transaction.internalId}-amount-${bucket.kind || bucket.name}-${index}`}
                        >
                          <Text variant='action3'>
                            {FormattingUtils.formatDollar(bucket.amount)}
                          </Text>
                        </Line>
                      ))}
                    </Stack>
                  )}
                </TableRowCell>
                <TableRowCell>
                  <Chip variant={transactionStatusVariant(transaction.status)}>
                    {transactionStatusLabel(transaction.status)}
                  </Chip>
                </TableRowCell>
              </TableRow>
            )
          })}
        />
      </>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  MyCommuterDetailsTransactions.displayName = 'MyCommuterDetailsTransactions'
}

export default MyCommuterDetailsTransactions
